import { createContext, useContext, useState, useEffect } from 'react';
import { supabase, api } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchUserData();
      } else {
        setLoading(false);
      }
    });

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          await fetchUserData();
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setFamily(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user's profile and family data
  async function fetchUserData() {
    try {
      const data = await api.get('/auth/me');
      setUser(prev => ({ ...prev, ...data.user }));
      setFamily(data.family);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Sign up with email/password
  async function signUp({ email, password, fullName, familyName, memberNames }) {
    setError(null);
    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName,
          family_name: familyName,
          member_names: memberNames
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Sign in with the new credentials
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        throw signInError;
      }

      setFamily(data.family);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign in with Google
  async function signInWithGoogle() {
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/create-family`
        }
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Create family for OAuth users
  async function createFamily({ familyName, memberNames }) {
    setError(null);
    try {
      const data = await api.post('/auth/google/callback', {
        family_name: familyName,
        member_names: memberNames
      });

      setFamily(data.family);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign out
  async function signOut() {
    setError(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setFamily(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  const value = {
    user,
    family,
    loading,
    error,
    signUp,
    signInWithGoogle,
    createFamily,
    signOut,
    // Computed state
    isAuthenticated: !!user,
    hasFamily: !!family,
    needsFamily: !!user && !family
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
